class VeloraDialog extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.onTrigger = this.onTrigger.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onClose = this.onClose.bind(this);
    document.addEventListener('click', this.onTrigger);
    this.dialog.addEventListener('click', this.onClick);
    this.dialog.addEventListener('close', this.onClose);
    this.initialized = true;
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onTrigger);
    this.dialog?.removeEventListener('click', this.onClick);
    this.dialog?.removeEventListener('close', this.onClose);
    this.initialized = false;
  }

  onTrigger(event) {
    const trigger = event.target.closest(`[aria-controls="${this.dialog.id}"]`);
    if (!trigger) return;
    event.preventDefault();
    this.opener = trigger;
    this.dialog.showModal();
  }

  onClick(event) {
    if (event.target.closest('[data-dialog-close]')) this.dialog.close();
    if (event.target === this.dialog) {
      const bounds = this.dialog.getBoundingClientRect();
      const inside = event.clientX >= bounds.left && event.clientX <= bounds.right && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
      if (!inside) this.dialog.close();
    }
  }

  onClose() {
    this.opener?.focus();
  }
}

if (!customElements.get('velora-dialog')) customElements.define('velora-dialog', VeloraDialog);

class QuickViewDialog extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.dialog = this.querySelector('dialog');
    this.body = this.querySelector('[data-quick-view-body]');
    if (!this.dialog || !this.body) return;
    this.onTrigger = this.onTrigger.bind(this);
    this.onClose = () => this.controller?.abort();
    document.addEventListener('click', this.onTrigger);
    this.dialog.addEventListener('close', this.onClose);
    this.initialized = true;
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onTrigger);
    this.dialog?.removeEventListener('close', this.onClose);
    this.controller?.abort();
    this.initialized = false;
  }

  async onTrigger(event) {
    const link = event.target.closest('[data-quick-view]');
    if (!link) return;
    event.preventDefault();
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.body.setAttribute('aria-busy', 'true');
    this.body.textContent = this.dataset.loading || '';
    const url = new URL(link.href);
    url.searchParams.set('section_id', 'quick-view-product');
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      this.body.innerHTML = await response.text();
      this.body.dispatchEvent(new CustomEvent('velora:content:loaded', { bubbles: true }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.body.textContent = this.dataset.error || '';
      const details = document.createElement('a');
      details.className = 'button button--text';
      details.href = link.href;
      details.textContent = this.dataset.viewDetails || '';
      this.body.append(details);
    } finally {
      if (this.controller === controller) this.body.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('quick-view-dialog')) customElements.define('quick-view-dialog', QuickViewDialog);

class VeloraMenu extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.addEventListener('keydown', this.onKeydown);
    this.initialized = true;
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.onKeydown);
    this.initialized = false;
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    const expanded = this.querySelector('details[open]');
    if (!expanded) return;
    expanded.removeAttribute('open');
    expanded.querySelector('summary')?.focus();
  }
}

if (!customElements.get('velora-menu')) customElements.define('velora-menu', VeloraMenu);

class VeloraHeader extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.enabled = this.dataset.hideOnScroll === 'true' && this.dataset.sticky === 'true';
    this.previous = Math.max(0, Number(window.scrollY) || 0);
    this.frame = 0;
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('focusin', this.reveal);
    this.addEventListener('click', this.reveal);
    this.initialized = true;
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('focusin', this.reveal);
    this.removeEventListener('click', this.reveal);
    window.cancelAnimationFrame(this.frame);
    this.initialized = false;
  }

  reveal = () => this.classList.remove('is-hidden');

  onScroll() {
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = 0;
      const position = Math.max(0, Number(window.scrollY) || 0);
      const movement = position - this.previous;
      const pinned = window.Shopify?.designMode || this.matches?.(':focus-within') || this.querySelector('details[open]') || document.querySelector('dialog[open]');
      this.classList.toggle('is-scrolled', position > 16);
      if (!this.enabled || pinned || position <= 120) this.reveal();
      else if (Math.abs(movement) >= 8) this.classList.toggle('is-hidden', movement > 0);
      if (Math.abs(movement) >= 8 || position <= 120) this.previous = position;
    });
  }
}

if (!customElements.get('velora-header')) customElements.define('velora-header', VeloraHeader);

class CollectionTabs extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    if (!this.tabs.length || !this.panels.length) return;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    const selectedTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    this.select(selectedTab, false);
    this.initialized = true;
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.initialized = false;
  }

  onClick(event) {
    const tab = event.target.closest('[role="tab"]');
    if (tab && this.contains(tab)) this.select(tab);
  }

  onKeydown(event) {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const currentIndex = this.tabs.indexOf(tab);
    let nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : currentIndex + (event.key === 'ArrowRight' ? 1 : -1);
    nextIndex = (nextIndex + this.tabs.length) % this.tabs.length;
    this.select(this.tabs[nextIndex]);
  }

  select(selectedTab, focus = true) {
    const panelId = selectedTab.getAttribute('aria-controls');
    this.tabs.forEach((tab) => {
      const active = tab === selectedTab;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      tab.classList.toggle('is-active', active);
    });
    this.panels.forEach((panel) => {
      const active = panel.id === panelId;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });
    if (focus) selectedTab.focus();
  }
}

if (!customElements.get('collection-tabs')) customElements.define('collection-tabs', CollectionTabs);

class AgeGate extends HTMLElement {
  connectedCallback() {
    if (this.initialized || this.dataset.enabled !== 'true') return;
    this.dialog = this.querySelector('dialog');
    this.confirmButton = this.querySelector('[data-age-confirm]');
    if (!this.dialog || !this.confirmButton) return;

    this.onConfirm = this.onConfirm.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onSectionSelect = this.onSectionSelect.bind(this);
    this.confirmButton.addEventListener('click', this.onConfirm);
    this.dialog.addEventListener('cancel', this.onCancel);
    document.addEventListener('shopify:section:select', this.onSectionSelect);
    this.initialized = true;

    if (!window.Shopify?.designMode && !this.isVerified()) this.dialog.showModal();
  }

  disconnectedCallback() {
    this.confirmButton?.removeEventListener('click', this.onConfirm);
    this.dialog?.removeEventListener('cancel', this.onCancel);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    this.initialized = false;
  }

  isVerified() {
    try {
      const expiry = Number(localStorage.getItem('recollection-age-verified-until'));
      return Number.isFinite(expiry) && expiry > Date.now();
    } catch (_error) {
      return false;
    }
  }

  onConfirm() {
    const days = Number(this.dataset.rememberDays) || 30;
    try {
      localStorage.setItem('recollection-age-verified-until', String(Date.now() + days * 86400000));
    } catch (_error) {}
    this.dialog.close();
  }

  onCancel(event) {
    event.preventDefault();
  }

  onSectionSelect(event) {
    if (event.target.contains(this) && !this.dialog.open) this.dialog.showModal();
  }
}

if (!customElements.get('age-gate')) customElements.define('age-gate', AgeGate);

class CountdownTimer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.endTime = Date.parse(this.dataset.endDate || '');
    this.days = this.querySelector('[data-days]');
    this.hours = this.querySelector('[data-hours]');
    this.minutes = this.querySelector('[data-minutes]');
    this.seconds = this.querySelector('[data-seconds]');
    this.status = this.querySelector('[data-countdown-status]');
    if (!Number.isFinite(this.endTime)) {
      this.classList.add('is-unconfigured');
      return;
    }

    this.update();
    if (this.endTime > Date.now()) this.interval = window.setInterval(() => this.update(), 1000);
    this.initialized = true;
  }

  disconnectedCallback() {
    window.clearInterval(this.interval);
    this.initialized = false;
  }

  update() {
    const remaining = Math.max(0, this.endTime - Date.now());
    const totalSeconds = Math.floor(remaining / 1000);
    const values = {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };
    if (this.days) this.days.textContent = String(values.days).padStart(2, '0');
    if (this.hours) this.hours.textContent = String(values.hours).padStart(2, '0');
    if (this.minutes) this.minutes.textContent = String(values.minutes).padStart(2, '0');
    if (this.seconds) this.seconds.textContent = String(values.seconds).padStart(2, '0');

    if (remaining === 0) {
      window.clearInterval(this.interval);
      this.classList.add('is-complete');
      if (this.status) this.status.textContent = this.dataset.completeMessage || '';
      if (this.dataset.hideOnComplete === 'true') this.hidden = true;
    }
  }
}

if (!customElements.get('countdown-timer')) customElements.define('countdown-timer', CountdownTimer);

class GiftCardRecipient extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.toggle = this.querySelector('[data-recipient-toggle]');
    this.fields = this.querySelector('[data-recipient-fields]');
    this.email = this.querySelector('[data-recipient-email]');
    this.sendOn = this.querySelector('[data-recipient-send-on]');
    this.offset = this.querySelector('[data-recipient-offset]');
    this.status = this.querySelector('[data-recipient-status]');
    if (!this.toggle || !this.fields) return;

    this.onChange = this.onChange.bind(this);
    this.toggle.addEventListener('change', this.onChange);
    this.setDateLimits();
    this.update(false);
    this.initialized = true;
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('change', this.onChange);
    this.initialized = false;
  }

  onChange() {
    this.update(true);
  }

  setDateLimits() {
    if (!this.sendOn) return;
    const today = new Date();
    const maximum = new Date(today);
    maximum.setDate(maximum.getDate() + 90);
    const toLocalISO = (date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    this.sendOn.min = toLocalISO(today);
    this.sendOn.max = toLocalISO(maximum);
  }

  update(announce) {
    const expanded = this.toggle.checked;
    this.toggle.setAttribute('aria-expanded', String(expanded));
    this.fields.hidden = !expanded;
    this.fields.querySelectorAll('input, textarea').forEach((input) => { input.disabled = !expanded; });
    if (this.email) this.email.required = expanded;
    if (this.offset) {
      this.offset.disabled = !expanded;
      this.offset.value = expanded ? String(new Date().getTimezoneOffset()) : '';
    }
    if (announce && this.status) {
      this.status.textContent = expanded ? window.veloraStrings?.recipientExpanded || '' : window.veloraStrings?.recipientCollapsed || '';
    }
  }
}

if (!customElements.get('gift-card-recipient')) customElements.define('gift-card-recipient', GiftCardRecipient);

class SectionVideoControls extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.scope = this.closest('[data-video-scope]');
    this.video = this.scope?.querySelector('video');
    this.button = this.querySelector('[data-video-toggle]');
    this.label = this.querySelector('[data-video-label]');
    if (!this.video || !this.button) return;
    this.initialized = true;
    this.active = false;
    this.visible = !('IntersectionObserver' in window);
    this.visitorPaused = false;
    this.suspended = false;
    this.playbackSession = {};
    this.playPending = false;
    this.showButton = this.dataset.showButton === 'true';
    this.showControls = this.dataset.showControls === 'true';
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    // Set playback properties before play(): Safari checks the live muted state.
    this.video.playsInline = true;
    if (this.dataset.autoplay === 'true') {
      this.video.defaultMuted = true;
      this.video.muted = true;
    }
    this.button.hidden = !this.showButton;
    this.video.controls = this.showControls;
    this.onState = (event) => {
      if (event.type === 'play') this.suspended = false;
      this.video.ended ? this.ensureManualPlayback() : this.updateButton();
    };
    this.onToggle = () => {
      this.visitorPaused = !this.video.paused;
      if (this.video.paused) this.play();
      else this.video.pause();
    };
    this.onPause = () => {
      if (this.video.paused && !this.suspended && this.visible && !document.hidden) this.visitorPaused = true;
    };
    this.onReady = () => this.tryAutoplay();
    this.onVisibility = () => document.hidden ? this.suspend() : this.tryAutoplay();
    this.onMotion = () => {
      if (this.motion.matches) {
        this.suspend();
        this.ensureManualPlayback();
      } else this.tryAutoplay();
    };
    this.button.addEventListener('click', this.onToggle);
    ['play', 'pause', 'ended'].forEach((event) => this.video.addEventListener(event, this.onState));
    this.video.addEventListener('pause', this.onPause);
    ['loadedmetadata', 'canplay'].forEach((event) => this.video.addEventListener(event, this.onReady));
    document.addEventListener('visibilitychange', this.onVisibility);
    this.motion.addEventListener?.('change', this.onMotion);
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible) this.tryAutoplay();
        else if (this.dataset.autoplay === 'true') this.suspend();
      });
      this.observer.observe(this.video);
    }
    const slide = this.scope.closest('[data-slide]');
    this.setActive(!slide || slide.getAttribute('aria-hidden') === 'false');
    this.updateButton();
  }

  setActive(active) {
    if (!this.initialized || this.active === active) return;
    this.active = active;
    if (!active) this.suspend();
    else if (this.dataset.autoplay === 'true' && !this.motion.matches) this.tryAutoplay();
    else this.ensureManualPlayback();
  }

  tryAutoplay() {
    if (!this.initialized || !this.active || !this.visible || document.hidden || this.motion.matches || this.visitorPaused || this.video.ended || this.dataset.autoplay !== 'true') return;
    if (this.playPending) {
      this.retryAfterPending = this.video.paused;
      return;
    }
    this.play(true);
  }

  suspend() {
    this.suspended = true;
    this.retryAfterPending = false;
    this.video.pause();
  }

  play(automatic = false) {
    if (this.playPending || !this.video.paused) return;
    const session = this.playbackSession;
    this.suspended = false;
    this.retryAfterPending = false;
    this.playPending = true;
    this.video.play().then(() => {
      if (!this.initialized || this.playbackSession !== session) return;
      this.playPending = false;
      if (automatic) this.video.controls = this.showControls;
    }, (error) => {
      if (!this.initialized || this.playbackSession !== session) return;
      this.playPending = false;
      // A visibility/slide change can interrupt a pending play request.
      if (error.name === 'AbortError' && this.retryAfterPending) this.tryAutoplay();
      else this.ensureManualPlayback();
    });
  }

  ensureManualPlayback() {
    // Keep playback reachable when autoplay is disabled or blocked.
    if (!this.showButton) this.video.controls = true;
    this.updateButton();
  }

  updateButton() {
    const label = this.video.paused ? this.button.dataset.playLabel : this.button.dataset.pauseLabel;
    this.button.setAttribute('aria-label', label);
    if (this.label) this.label.textContent = label;
  }

  disconnectedCallback() {
    if (!this.initialized) return;
    this.initialized = false;
    this.observer?.disconnect();
    this.button.removeEventListener('click', this.onToggle);
    ['play', 'pause', 'ended'].forEach((event) => this.video.removeEventListener(event, this.onState));
    this.video.removeEventListener('pause', this.onPause);
    ['loadedmetadata', 'canplay'].forEach((event) => this.video.removeEventListener(event, this.onReady));
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.motion.removeEventListener?.('change', this.onMotion);
    this.video.pause();
  }
}

if (!customElements.get('section-video-controls')) customElements.define('section-video-controls', SectionVideoControls);

class MediaSlideshow extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.rail = this.dataset.contentRail === 'true';
    this.track = this.querySelector('[data-slideshow-track], [data-rail-track]');
    this.slides = [...this.querySelectorAll('[data-slide], [data-rail-item]')];
    this.previous = this.querySelector('[data-slideshow-previous], [data-rail-previous]');
    this.next = this.querySelector('[data-slideshow-next], [data-rail-next]');
    this.pause = this.querySelector('[data-slideshow-pause]');
    this.progress = this.querySelector('[data-rail-progress]');
    this.dots = [...this.querySelectorAll('[data-slide-target]')];
    if (!this.track || !this.slides.length) return;

    this.currentIndex = 0;
    this.hasAdvanced = false;
    this.paused = this.rail || this.dataset.autoplay !== 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.delay = Math.max(3000, Number(this.dataset.delay) || 6000);
    this.initialDelay = Math.max(12000, this.delay);
    this.onScroll = this.onScroll.bind(this);
    this.onPrevious = () => this.goTo(this.currentIndex - 1, true);
    this.onNext = () => this.goTo(this.currentIndex + 1, true);
    this.onPause = () => this.togglePause();
    this.onVisibility = () => document.hidden ? this.stopTimer() : this.startTimer();
    this.onPointerEnter = () => this.stopTimer();
    this.onPointerLeave = () => this.startTimer();
    this.onFocusIn = () => this.stopTimer();
    this.onFocusOut = (event) => { if (!this.contains(event.relatedTarget)) this.startTimer(); };
    this.onBlockSelect = (event) => {
      const item = event.target.closest?.('[data-rail-item]');
      if (this.rail && item && this.contains(item)) this.goTo(this.slides.indexOf(item), true);
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.previous?.addEventListener('click', this.onPrevious);
    this.next?.addEventListener('click', this.onNext);
    this.pause?.addEventListener('click', this.onPause);
    this.dotHandlers = this.dots.map((dot) => {
      const handler = () => this.goTo(Number(dot.dataset.slideTarget), true);
      dot.addEventListener('click', handler);
      return [dot, handler];
    });
    this.addEventListener('pointerenter', this.onPointerEnter);
    this.addEventListener('pointerleave', this.onPointerLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('visibilitychange', this.onVisibility);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    this.update();
    this.startTimer();
    this.initialized = true;
  }

  disconnectedCallback() {
    this.stopTimer();
    window.cancelAnimationFrame(this.scrollFrame);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.previous?.removeEventListener('click', this.onPrevious);
    this.next?.removeEventListener('click', this.onNext);
    this.pause?.removeEventListener('click', this.onPause);
    this.dotHandlers?.forEach(([dot, handler]) => dot.removeEventListener('click', handler));
    this.removeEventListener('pointerenter', this.onPointerEnter);
    this.removeEventListener('pointerleave', this.onPointerLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.initialized = false;
  }

  onScroll() {
    window.cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = window.requestAnimationFrame(() => {
      const trackLeft = this.track.getBoundingClientRect().left;
      const distances = this.slides.map((slide) => Math.abs(slide.getBoundingClientRect().left - trackLeft));
      const closest = distances.indexOf(Math.min(...distances));
      if (closest !== this.currentIndex) {
        this.currentIndex = closest;
        this.update();
      } else if (this.rail) this.update();
    });
  }

  goTo(index, userInitiated = false) {
    const looping = this.dataset.loop === 'true';
    let targetIndex = index;
    if (looping) targetIndex = (index + this.slides.length) % this.slides.length;
    else targetIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    this.currentIndex = targetIndex;
    if (userInitiated) this.hasAdvanced = true;
    this.track.scrollTo({ left: this.slides[targetIndex].offsetLeft, behavior: userInitiated ? 'smooth' : 'auto' });
    this.update();
    if (userInitiated) this.restartTimer();
  }

  update() {
    if (this.rail) {
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      const position = Math.abs(this.track.scrollLeft);
      const overflowing = maximum > 2;
      const value = overflowing ? position / maximum : 1;
      this.dataset.overflow = overflowing;
      this.style.setProperty('--rail-progress', value);
      this.progress?.setAttribute('aria-valuenow', Math.round(value * 100));
      if (this.previous) this.previous.disabled = !overflowing || position <= 2;
      if (this.next) this.next.disabled = !overflowing || maximum - position <= 2;
      return;
    }
    this.slides.forEach((slide, index) => {
      const active = index === this.currentIndex;
      slide.setAttribute('aria-hidden', String(!active));
      slide.inert = !active;
      slide.querySelector('section-video-controls')?.setActive(active);
    });
    this.dots.forEach((dot, index) => {
      const active = index === this.currentIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
    if (this.dataset.loop !== 'true') {
      if (this.previous) this.previous.disabled = this.currentIndex === 0;
      if (this.next) this.next.disabled = this.currentIndex === this.slides.length - 1;
    }
  }

  togglePause() {
    this.paused = !this.paused;
    this.pause?.setAttribute('aria-pressed', String(this.paused));
    const label = this.paused ? this.pause?.dataset.playLabel : this.pause?.dataset.pauseLabel;
    if (this.pause && label) {
      this.pause.setAttribute('aria-label', label);
      this.pause.textContent = label;
    }
    this.paused ? this.stopTimer() : this.startTimer();
  }

  startTimer() {
    if (this.paused || this.slides.length < 2 || document.hidden) return;
    this.stopTimer();
    const wait = this.hasAdvanced ? this.delay : this.initialDelay;
    this.timer = window.setTimeout(() => {
      if (this.dataset.loop !== 'true' && this.currentIndex === this.slides.length - 1) {
        this.stopTimer();
        return;
      }
      this.hasAdvanced = true;
      this.goTo(this.currentIndex + 1);
      this.startTimer();
    }, wait);
  }

  stopTimer() {
    window.clearTimeout(this.timer);
  }

  restartTimer() {
    this.stopTimer();
    this.startTimer();
  }
}

if (!customElements.get('media-slideshow')) customElements.define('media-slideshow', MediaSlideshow);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-autofocus-on-load]').forEach((element) => element.focus());
});
